import { useCallback, useEffect, useState } from "react"
import { Bubble } from "./Bubble"
import { View } from "react-native"

type Props = {
  blended?: boolean
  offset?: { x?: number; y?: number }
}

export const GravitationalBubbles = ({ blended, offset }: Props) => {
  const polarFromCartesian = (coordinates: { x: number; y: number }) => {
    let angle = 0

    if (coordinates.x !== 0) {
      angle = Math.atan2(coordinates.y, coordinates.x)
    }

    return {
      r: Math.sqrt(Math.pow(coordinates.x, 2) + Math.pow(coordinates.y, 2)),
      theta: angle,
    }
  }
  // h: timestep
  // u: variables
  // derivative: function that calculates the derivatives
  const rungeKuttaCalc = (h: number, u: number[], derivative: () => number[]) => {
    const a = [h / 2, h / 2, h, 0]
    const b = [h / 6, h / 3, h / 3, h / 6]
    const u0 = []
    const ut = []
    const dimension = u.length

    for (let i = 0; i < dimension; i++) {
      u0.push(u[i])
      ut.push(0)
    }

    for (let j = 0; j < 4; j++) {
      const du = derivative()

      for (let i = 0; i < dimension; i++) {
        u[i] = u0[i] + a[j] * du[i]
        ut[i] = ut[i] + b[j] * du[i]
      }
    }

    for (let i = 0; i < dimension; i++) {
      u[i] = u0[i] + ut[i]
    }
  }

  // Current state of the system
  const state = {
    // State variables used in the differential equations
    // First two elements are x and y positions, and second two are x and y components of velocity
    // repeated for three bodies.
    u: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  }

  const [bodiesState, setBodiesState] = useState([
    {
      x: state.u[0],
      y: state.u[1],
      color: "#9BB454",
      innerColor: "#FFECCF",
    },
    {
      x: state.u[4],
      y: state.u[5],
      color: "#286FFF",
      innerColor: "#D8B8FB",
    },
    {
      x: state.u[8],
      y: state.u[9],
      color: "#286FFF",
      innerColor: "#D8B8FB",
    },
  ])

  const vigure8Position = { x: 0.97000436, y: -0.24308753 }
  const vigure8Velocity = { x: -0.93240737, y: -0.86473146 }

  // Initial condition of the model. The conditions are loaded from the currently selected simulation.
  const initialConditions = {
    bodies: 3, // Number of bodies
    masses: [1, 1, 1],
    positions: [
      polarFromCartesian({ x: vigure8Position.x, y: vigure8Position.y }),
      polarFromCartesian({ x: -vigure8Position.x, y: -vigure8Position.y }),
      polarFromCartesian({ x: 0, y: 0 }),
    ],
    velocities: [
      polarFromCartesian({ x: -vigure8Velocity.x / 2, y: -vigure8Velocity.y / 2 }),
      polarFromCartesian({ x: -vigure8Velocity.x / 2, y: -vigure8Velocity.y / 2 }),
      polarFromCartesian({ x: vigure8Velocity.x, y: vigure8Velocity.y }),
    ],
    dimensionless: true,
  }

  const calculateCenterOfMassVelocity = useCallback(() => {
    const centerOfMassVelocity = { x: 0, y: 0 }
    let sumOfMasses = 0

    // Loop through the bodies
    for (let iBody = 0; iBody < initialConditions.bodies; iBody++) {
      const bodyStart = iBody * 4 // Starting index for current body in the u array
      centerOfMassVelocity.x += initialConditions.masses[iBody] * state.u[bodyStart + 2]
      centerOfMassVelocity.y += initialConditions.masses[iBody] * state.u[bodyStart + 3]
      sumOfMasses += initialConditions.masses[iBody]
    }

    centerOfMassVelocity.x /= sumOfMasses
    centerOfMassVelocity.y /= sumOfMasses

    return centerOfMassVelocity
  }, [initialConditions.bodies, initialConditions.masses, state.u])

  const calculateCenterOfMass = useCallback(() => {
    const centerOfMass = { x: 0, y: 0 }
    let sumOfMasses = 0

    // Loop through the bodies
    for (let iBody = 0; iBody < initialConditions.bodies; iBody++) {
      const bodyStart = iBody * 4 // Starting index for current body in the u array
      centerOfMass.x += initialConditions.masses[iBody] * state.u[bodyStart + 0]
      centerOfMass.y += initialConditions.masses[iBody] * state.u[bodyStart + 1]
      sumOfMasses += initialConditions.masses[iBody]
    }

    centerOfMass.x /= sumOfMasses
    centerOfMass.y /= sumOfMasses

    return centerOfMass
  }, [initialConditions.bodies, initialConditions.masses, state.u])

  const resetStateToInitialConditions = useCallback(() => {
    // Loop through the bodies
    for (let iBody = 0; iBody < initialConditions.bodies; iBody++) {
      const bodyStart = iBody * 4 // Starting index for current body in the u array

      const position = initialConditions.positions[iBody]
      state.u[bodyStart + 0] = position.r * Math.cos(position.theta) // x
      state.u[bodyStart + 1] = position.r * Math.sin(position.theta) //y

      const velocity = initialConditions.velocities[iBody]
      state.u[bodyStart + 2] = velocity.r * Math.cos(velocity.theta) // velocity x
      state.u[bodyStart + 3] = velocity.r * Math.sin(velocity.theta) // velocity y
    }

    const centerOfMassVelocity = calculateCenterOfMassVelocity()
    const centerOfMass = calculateCenterOfMass()

    // Correct the velocities and positions of the bodies
    // to make the center of mass motionless at the middle of the screen
    for (let iBody = 0; iBody < initialConditions.bodies; iBody++) {
      const bodyStart = iBody * 4 // Starting index for current body in the u array
      state.u[bodyStart + 0] -= centerOfMass.x
      state.u[bodyStart + 1] -= centerOfMass.y
      state.u[bodyStart + 2] -= centerOfMassVelocity.x
      state.u[bodyStart + 3] -= centerOfMassVelocity.y
    }
  }, [
    calculateCenterOfMass,
    calculateCenterOfMassVelocity,
    initialConditions.bodies,
    initialConditions.positions,
    initialConditions.velocities,
    state.u,
  ])

  // Calculates the acceleration of the body 'iFromBody'
  // due to gravity from other bodies,
  // using Newton's law of gravitation.
  //   iFromBody: the index of body. 0 is first body, 1 is second body.
  //   coordinate: 0 for x coordinate, 1 for y coordinate
  const acceleration = useCallback(
    (iFromBody: number, coordinate: number) => {
      let result = 0
      const iFromBodyStart = iFromBody * 4 // Starting index for the body in the u array

      // Loop through the bodies
      for (let iToBody = 0; iToBody < initialConditions.bodies; iToBody++) {
        if (iFromBody === iToBody) {
          continue
        }
        const iToBodyStart = iToBody * 4 // Starting index for the body in the u array

        // Distance between the two bodies
        const distanceX = state.u[iToBodyStart + 0] - state.u[iFromBodyStart + 0]
        const distanceY = state.u[iToBodyStart + 1] - state.u[iFromBodyStart + 1]
        const distance = Math.sqrt(Math.pow(distanceX, 2) + Math.pow(distanceY, 2))

        result +=
          (initialConditions.masses[iToBody] *
            (state.u[iToBodyStart + coordinate] - state.u[iFromBodyStart + coordinate])) /
          Math.pow(distance, 3)
      }

      return result
    },
    [initialConditions.bodies, initialConditions.masses, state.u]
  )

  // Calculate the derivatives of the system of ODEs that describe equation of motion of the bodies
  const derivative = useCallback(() => {
    const du = new Array<number>(initialConditions.bodies * 4)

    // Loop through the bodies
    for (let iBody = 0; iBody < initialConditions.bodies; iBody++) {
      // Starting index for current body in the u array
      const bodyStart = iBody * 4

      du[bodyStart + 0] = state.u[bodyStart + 0 + 2] // Velocity x
      du[bodyStart + 1] = state.u[bodyStart + 0 + 3] // Velocity y
      du[bodyStart + 2] = acceleration(iBody, 0) // Acceleration x
      du[bodyStart + 3] = acceleration(iBody, 1) // Acceleration y
    }

    return du
  }, [acceleration, initialConditions.bodies, state.u])

  // The main function that is called on every animation frame.
  // It calculates and updates the current positions of the bodies
  const updatePosition = useCallback(
    (timestep: number) => {
      rungeKuttaCalc(timestep, state.u, derivative)
      setBodiesState([
        {
          x: state.u[0],
          y: state.u[1],
          color: bodiesState[0].color,
          innerColor: bodiesState[0].innerColor,
        },
        {
          x: state.u[4],
          y: state.u[5],
          color: bodiesState[1].color,
          innerColor: bodiesState[1].innerColor,
        },
        {
          x: state.u[8],
          y: state.u[9],
          color: bodiesState[2].color,
          innerColor: bodiesState[2].innerColor,
        },
      ])
    },
    [bodiesState, derivative, state.u]
  )

  useEffect(() => {
    resetStateToInitialConditions()
    const interval = setInterval(() => updatePosition(0.002), 200)
    return () => {
      clearInterval(interval)
    }
  }, [resetStateToInitialConditions, updatePosition])

  return (
    <View style={{ opacity: blended ? 0.2 : 1 }}>
      {bodiesState.map((body, index) => (
        <Bubble
          key={index}
          color={body.color}
          innerColor={body.innerColor}
          x={body.x * 500 + (offset?.x ?? 0)}
          y={body.y * 500 + (offset?.y ?? 300)}
        />
      ))}
    </View>
  )
}
